import {
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  Typography,
} from '@mui/material';
import React, { useState } from 'react';

import CustomButton from '../Button';
import { Header } from '../Table';
import { exportToCsv } from './utils/exportToCsv';
import { exportToExcel } from './utils/exportToExcel';
import { expandHeaders } from './utils/exportUtils';

interface ExportDialogProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  data: any[];
  headers: Header[];
  fileName?: string;
  fileType: 'csv' | 'xlsx';
  complete?: boolean;
  searchData?: any;
  setLoading?: any;
}

const TableExportDialog: React.FC<ExportDialogProps> = (props) => {
  const {
    open,
    setOpen,
    data,
    headers,
    fileName,
    fileType,
    complete,
    searchData,
    setLoading,
  } = props;
  const [loading, setLoadingState] = useState(false);

  // Export options
  // Filter out action headers (those with handleAction property)
  const exportableHeaders = headers;

  // Expand headers for special types to show individual columns
  const expandedHeaders = expandHeaders(exportableHeaders);

  const [selectedColumns, setSelectedColumns] = useState<string[]>(
    expandedHeaders.map((h) => h.key),
  );

  const handleExport = async () => {
    setLoadingState(true);
    if (setLoading) setLoading(true);

    try {
      let exportData = data;

      // If complete export is requested, fetch all data
      if (complete && searchData) {
        const response = await searchData({ exportComplete: true });
        exportData = response.data;
      }

      // Filter headers based on selected columns
      const filteredHeaders = expandedHeaders.filter((h) =>
        selectedColumns.includes(h.key),
      );

      // Export based on file type
      switch (fileType) {
        case 'csv':
          exportToCsv(exportData, filteredHeaders, fileName);
          break;
        case 'xlsx':
          exportToExcel(exportData, filteredHeaders, fileName);
          break;
        default:
          break;
      }
    } catch (error) {
      console.error('Export error:', error);
    } finally {
      setLoadingState(false);
      if (setLoading) setLoading(false);
      setOpen(false);
    }
  };

  return (
    <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
      <DialogTitle>Opciones de exportación</DialogTitle>
      <DialogContent>
        {loading && (
          <div
            style={{
              minHeight: 100,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <Typography>Exportando...</Typography>
          </div>
        )}
        {!loading && (
          <div>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography variant="subtitle2" style={{ marginBottom: '8px' }}>
                  Seleccionar columnas:
                </Typography>
                <Grid container spacing={1}>
                  {expandedHeaders.map((column) => (
                    <Grid item xs={4} key={column.key}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={selectedColumns.includes(column.key)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedColumns([
                                  ...selectedColumns,
                                  column.key,
                                ]);
                              } else {
                                setSelectedColumns(
                                  selectedColumns.filter(
                                    (key) => key !== column.key,
                                  ),
                                );
                              }
                            }}
                            color="primary"
                            size="small"
                          />
                        }
                        label={column.label}
                        style={{ margin: 0 }}
                      />
                    </Grid>
                  ))}
                </Grid>
              </Grid>
            </Grid>
          </div>
        )}
      </DialogContent>
      <DialogActions>
        <CustomButton color="secondary" onClick={() => setOpen(false)}>
          Cancelar
        </CustomButton>
        <CustomButton color="primary" onClick={handleExport} disabled={loading}>
          Exportar
        </CustomButton>
      </DialogActions>
    </Dialog>
  );
};

export default TableExportDialog;
